import { z } from 'zod';
import { CARE_TEAM_ROLE } from '../appointment-transitions';
export const NOTE_TEMPLATE_FIELDS = [
    'presentingComplaints',
    'historyOfPresentIllness',
    'pastHistory',
    'familyHistory',
    'socialHistory',
    'drugHistory',
    'examination',
    'investigationsAdvised',
    'treatmentPlan',
    'advice',
];
const MAX_TEMPLATE_BYTES = 20 * 1024;
export const noteTemplateSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    departmentId: z.string().uuid().nullable().optional(),
    shared: z.boolean().default(false),
    fields: z.record(z.enum(NOTE_TEMPLATE_FIELDS), z.string().max(8000)),
    isActive: z.boolean().default(true),
})
    .superRefine((value, context) => {
    if (Object.keys(value.fields).length === 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['fields'], message: 'A template fills at least one part of the note' });
    }
    if (new TextEncoder().encode(JSON.stringify(value.fields)).length > MAX_TEMPLATE_BYTES) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['fields'], message: 'A template can hold at most 20 KB of text' });
    }
});
export function applyTemplate(current, template) {
    const merged = { ...current };
    for (const field of NOTE_TEMPLATE_FIELDS) {
        const text = template[field];
        if (!text)
            continue;
        const existing = merged[field]?.trim();
        merged[field] = existing ? `${existing}\n${text}` : text;
    }
    return merged;
}
export const drugSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(80),
});
export const voidReasonSchema = z.object({
    reason: z.string().trim().min(5, 'Say why in a few words').max(400),
});
export const feeWaiverRequestSchema = z.object({
    reason: z.string().trim().min(10, 'Say in a sentence why the fee should be waived').max(400),
});
export const cosignSchema = z.object({
    note: z.string().trim().max(1000).optional(),
});
export const cosignQueueQuerySchema = z.object({
    departmentId: z.string().uuid().optional(),
    branchId: z.string().uuid().optional(),
    cursor: z.string().uuid().optional(),
});
export const careTeamMemberSchema = z.object({
    userId: z.string().uuid(),
    role: z.enum(CARE_TEAM_ROLE),
});
export const recordEncounterFormSchema = z.object({
    formCode: z.string().trim().min(2).max(60),
    data: z.record(z.string(), z.unknown()),
    supersedesId: z.string().uuid().optional(),
});
export const opdDuesQuerySchema = z.object({
    branchId: z.string().uuid(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    cursor: z.string().uuid().optional(),
});
export const opdPageQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().uuid().optional(),
});
export const opdReportRangeSchema = z
    .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    branchId: z.string().uuid().optional(),
    cursor: z.string().uuid().optional(),
})
    .refine((value) => value.from <= value.to, { path: ['to'], message: 'The range must end after it starts' })
    .refine((value) => (Date.parse(value.to) - Date.parse(value.from)) / 86_400_000 <= 93, {
    path: ['to'],
    message: 'Pick at most 93 days',
});
//# sourceMappingURL=opd-emr.js.map